/**
 * 最初の公開で見せない機能（founder 決定 2026-09-27・docs/MASTER-PLAN.md §1c）。ダッシュボードの切り替えはこの 1 つの定数だけ。
 *
 * WHY 見せないか: Kumo はクリエイターに「フラットなバックエンド」として出す。ランキング・実績・通貨・ソーシャル・運営機能のような
 *   「ゲーム向けの出来合いの機能」は Kumo の機能として並べず、クリエイター自身の DB と Functions に組む Skill（レシピ）で配る
 *   （コードも責任もクリエイターのもの。ずるの防止やアイテムの設計はクリエイターが決める）。AI の窓口も最初は出さない。
 * WHY コードを消さないか: 後で出す判断に備える。ここから外せば、ナビ・ルート・機能の一覧・プレイヤーの詳細が元どおり出る。
 * 例外: プレイヤーの BAN は残す（moderation = 通報・制裁を掛ける を隠しても、PlayerDrawer の BAN / 解除は別の API で動く）。
 *   BAN の異議申し立ての一覧と回答（components/AppealsPanel.tsx・Players の画面）も moderation の外 = 常に出す
 *   （2026-09-27 appeal-tools・Quality SEC-HIDE-03。サーバーも異議の口を機能の切り替えの外に置いている = featureGate.ts の ALWAYS_ON）。
 *
 * キーはサーバーの機能の登録簿（apps/server/src/modules/config/features.ts）のキーそのもの。実在はテスト（launchHidden.test.ts）が確かめる。
 * 隠す場所は 7 か所あり（公開サイト・ダッシュボード・CLI・MCP・SDK・テンプレートの AGENTS.md・サーバー）、それぞれが同じ一覧を持つ。
 *   WHY 共有パッケージにしないか: ダッシュボード・CLI・MCP・SDK は依存を持たない作り（CLI は依存ゼロ・SDK は小ささが命）で、
 *   一覧 1 つのためにパッケージを足すより、各所の 1 定数 + サーバーの登録簿との突き合わせのテストの方が軽い。
 *   ON にできないようにするのはサーバー（Core）の仕事。ここは「見せない」だけ（直接 URL を開いた人もルートで概要へ戻す）。
 *
 * このファイルは import を持たない（featureToggle.ts と同じ理由: `node --test` で型の除去だけで動かす。pendingAction.ts が拡張子つきで読む）。
 */

/** 最初の公開で見せない機能のキー（サーバーの features.ts の登録簿のキー）。空にすれば全部出る */
export const LAUNCH_HIDDEN_FEATURES: readonly string[] = [
  // ランキング・実績（Skill で作る。ランキングの Skill は各テンプレートの .claude/skills/leaderboard/）
  'leaderboards',
  'achievements',
  // 交換所。所持品（inventory）の「読む・使う」は founder 案 A（2026-09-27・MASTER-PLAN §1c）で出す = この一覧に無い
  // （課金のコインとリワード広告の報酬の受け取り先。ON / OFF はクリエイターが機能の一覧で切り替える）。
  // ただし所持品の中の「読む・使う」以外（他人の分を見る・付与）は下の LAUNCH_HIDDEN_PARTS で隠したまま。
  // 2026-09-29: 課金と広告が両方隠れている間は所持品ごと隠す（下の INVENTORY_CARRIERS = サーバーの OPEN_PARTS_CARRIERS と同じ）
  'exchanges',
  // ログインボーナス / プレゼント / シリアル / イベント / 無料ガチャ
  'loginBonuses',
  'gifts',
  'serials',
  'events',
  'gachas',
  // フレンド（とフレンドのオンライン状態）/ チャット / ギルド。presence はフレンドに依存する（requires friends）ので一緒に隠す
  'friends',
  'presence',
  'chat',
  'guilds',
  // 通報と制裁を掛ける（プレイヤーの BAN と異議申し立ては残す = 上の冒頭の例外）
  'moderation',
  // AI の窓口
  'ai',
  // お金の後回し（2026-09-29 creator/launch-money・founder「お金に関しては後でいい」MASTER-PLAN §0・first-launch-money-api.md）。
  // payments = 利用者への販売（商品・購入・返金・ストア課金の設定）。サーバーは Core が閉じた（hiddenFeatures.ts）。
  // 前払い・利用料・残高・3DS（前払いの入金の分）・招待クレジットは出したまま（ここに載せない）
  // ads（広告の ON / OFF = 機能の一覧の ads.enabled・設定の確認の画面の ads の節）は 2026-10-19 creator/ads-open で出した
  //   （founder「広告を作り手に開ける準備（手順 1〜5）を始めて」・docs/teams/creator/tasks/ads-open.md）。運び手の広告が出たので
  //   所持品（inventory = リワード広告の報酬の受け取り先）も下の INVENTORY_CARRIERS の条件で一緒に出る。売上と出金（payouts）は閉じたまま。
  //   閉じ直すならここに 'ads' を戻すだけ（所持品も一緒に隠れる）
  'payments',
  // クリエイターから利用者へのメール（2026-09-29 creator/mail-close・Kumo 管理の決定）。初回の公開では配信・予約・定期配信を閉じる
  //   （メールの画面 pages/mail/・ナビ・ルート・機能の一覧・設定の確認の画面の節の名前）。サーバーは Core が閉じた（hiddenFeatures.ts の mail と
  //   HIDDEN_MAIL_ROUTES）。WHY 閉じるか: 送り過ぎ（スパム）の守りの 1 日の上限は「受け取りの設定（本人確認）で上がる」作りで、
  //   出金（payouts）を閉じた今は上げる道が無い = 半端な機能として出すより、まとめて後で出す。
  //   残す（案 A）: 利用者のログインのメール（emailLogin = 芯）と「XKumo からのお知らせ」の欄（kumoNews.optIn = components/KumoNewsCard.tsx・
  //   kumoNews.ts。features.mail と無関係の別の口で、送るのは運営だけ）。どちらも機能のキーが別なのでここに載せない
  'mail'
];

/**
 * サーバーの機能のキーではない閉じる対象（Core の hiddenFeatures.ts の RouteOnlyFeature と同じ名前）。
 *   payouts … クリエイター本人の売上と出金（Kumo アカウントの単位 = お金の画面の 売上と残高・売上レポート・出金・受け取りの登録・出金用のパスキー・
 *             前払いの「売上から先に充てる」）。features.ts には無い（ゲームごとに ON にするものではない）
 * WHY 別の一覧か: LAUNCH_HIDDEN_FEATURES は「サーバーの登録簿に実在するキー」をテストで確かめている（打ち間違いで隠し損ねない）。
 *   payouts は登録簿に無いので、こちらは hiddenFeatures.ts の ROUTE_ONLY と突き合わせる（launchHidden.test.ts）
 */
export const LAUNCH_HIDDEN_ROUTE_ONLY: readonly string[] = ['payouts'];

/**
 * 所持品（inventory）を開けておく理由になる機能（Core の hiddenFeatures.ts OPEN_PARTS_CARRIERS と同じ・2026-09-29）。
 * WHY: 所持品の「読む・使う」（founder 案 A）は課金のコインと広告の報酬の受け取り先として出した。課金と広告が**両方**隠れている間は
 *   受け取る物が増えない = サーバーも所持品の口を閉じる。画面も同じに合わせる（機能の一覧・雛形の通貨）。
 *   どちらかを一覧から外せば（= 再開）、所持品も元どおり出る（切り替えは 1 つのまま）
 */
export const INVENTORY_CARRIERS: readonly string[] = ['payments', 'ads'];

const BASE_HIDDEN = new Set([...LAUNCH_HIDDEN_FEATURES, ...LAUNCH_HIDDEN_ROUTE_ONLY]);
const HIDDEN = new Set([...BASE_HIDDEN, ...(INVENTORY_CARRIERS.every((k) => BASE_HIDDEN.has(k)) ? ['inventory'] : [])]);

/** この機能を画面に出さないか（LAUNCH_HIDDEN_FEATURES・LAUNCH_HIDDEN_ROUTE_ONLY・課金と広告が両方隠れている間の inventory） */
export function isLaunchHidden(key: string): boolean {
  return HIDDEN.has(key);
}

/**
 * 設定（kumo.config.json）の一番上の節のうち、隠す機能の中身そのものの節（2026-09-28 管理が /confirm を練習用の環境で確かめた結果・§1c）。
 * 使う所: リンクで確認の画面（pendingAction.ts の config.push の「変わる項目」）。本番の config が空のときに push すると、本体の summary の
 *   changedSections に既定値で埋まった 22 の節が全部並び、leaderboards・achievements・gachas などの名前が画面に出ていた。
 * 節と機能の対応: leaderboards / achievements / exchanges / events / loginBonuses / gachas は同じ名前の機能・social = friends・presence・chat・guilds。
 *   currencies / items はゲーム内の経済の定義（通貨・アイテムの一覧）= 交換所・ガチャ・ログインボーナス・実績の報酬の材料で、§1c で打ち出さない物
 *   （所持品の「読む・使う」は機能の一覧に出すが、定義の節の名前を確認の画面に並べる理由は無い。管理の指示 2026-09-28）。
 *   stats はランキングの材料だが汎用のデータ（サーバーの hiddenFeatures.ts も閉じない）= 出す。
 * 二重の守り: 本体の summary も Core が直す予定。形が変わったら pendingAction.ts を合わせる
 */
export const LAUNCH_HIDDEN_CONFIG_SECTIONS: readonly string[] = [
  'leaderboards',
  'achievements',
  'currencies',
  'items',
  'exchanges',
  'events',
  'loginBonuses',
  'gachas',
  'social'
];

/**
 * 機能と名前の違う設定の節 → その節が属する機能（2026-09-29 creator/launch-money）。機能が隠れている間だけ節の名前も出さない。
 *   products … 課金（payments）の商品の一覧。ads は機能と同じ名前なので上の「隠す機能と同じ名前」で落ちる
 * WHY 上の一覧に直接書かないか: 課金を再開する（LAUNCH_HIDDEN_FEATURES から payments を外す）だけで products も戻るように
 */
const SECTION_FEATURES: Record<string, string> = { products: 'payments' };

const HIDDEN_SECTIONS = new Set(LAUNCH_HIDDEN_CONFIG_SECTIONS);

/** この設定の節の名前を画面に出さないか（上の一覧か、隠す機能と同じ名前 = 後から節が増えても機能の一覧から落ちる・機能に属する節） */
export function isConfigSectionHidden(section: string): boolean {
  const owner = SECTION_FEATURES[section];
  return HIDDEN_SECTIONS.has(section) || HIDDEN.has(section) || (owner !== undefined && HIDDEN.has(owner));
}

/**
 * 出す機能の中で、一部だけまだ見せない部分（「機能.部分」）。サーバーの一部だけ開けた口（Core の hiddenFeatures.ts OPEN_PARTS）と対。
 *   inventory.admin … 所持品の管理の口（付与・他人の所持品の参照）。画面ではプレイヤーの詳細（PlayerDrawer）の所持品 = 開発者が他人の所持品を見る。
 *     名前はサイト（apps/site/src/features.mjs FEATURE_PARTS）・MCP とそろえる。Core は他人の分を読む口
 *     （GET /v1/admin/players/:playerId/inventory）と付与（…/grant）を閉じたまま = 開けたのはプレイヤー本人の GET /v1/inventory と
 *     POST /v1/inventory/consume だけ。画面だけ出すと「閉じた口」が見えてしまうので、案 A の範囲（本人が読む・使う）にそろえる
 * WHY 機能のキーとは別の一覧か: 機能の一覧（FeaturesCard）には inventory を出して ON / OFF させたいが、画面の一部は隠したい。
 *   機能のキーをもう 1 つ作るとサーバーの登録簿と食い違う（テストが登録簿と突き合わせる）ので、ダッシュボードの中だけの印にした
 */
export const LAUNCH_HIDDEN_PARTS: readonly string[] = ['inventory.admin'];

/**
 * 出したままの画面の中で、隠す機能に属する欄（「機能.欄」・2026-09-29 creator/launch-money）。機能が隠れている間は isPartHidden が true。
 *   payments.threeDSecure … 3D セキュアの集計の画面の「アプリ内の購入」（kind=purchases・ゲームごとの行）。前払いの入金の分だけ見せる
 * WHY LAUNCH_HIDDEN_PARTS と分けるか: あちらは「出す機能の中で常に隠す部分」（テストが「機能は出す」ことを確かめている）。
 *   こちらは機能ごと隠れている間だけ隠れる印で、isPartHidden の「機能ごと隠れていれば部分も隠れる」でそのまま決まる = 一覧は説明のためだけ
 */
export const LAUNCH_FEATURE_PARTS: readonly string[] = ['payments.threeDSecure'];

const HIDDEN_PARTS = new Set(LAUNCH_HIDDEN_PARTS);

/** 出す機能の中のこの部分を画面に出さないか（機能ごと隠れていれば当然隠れる） */
export function isPartHidden(part: string): boolean {
  return HIDDEN_PARTS.has(part) || HIDDEN.has(part.split('.')[0]!);
}

/**
 * プロジェクトのナビ（ProjectShell の NAV）の項目 → その画面が見せる機能。すべてが隠す機能なら、項目もルートも出さない。
 * WHY 「すべて」か: 1 つでも表に出す機能を見せる画面なら、その画面は残して中の隠す部分だけを外す方が迷わない
 *   （ランキングの画面は ランキング + 実績 だけなので丸ごと隠れる）
 */
const NAV_FEATURES: Record<string, readonly string[]> = {
  leaderboards: ['leaderboards', 'achievements'],
  // 課金の画面（PaymentsPage = 売上・直近の購入・返金・受け取りの状態）は payments だけ（2026-09-29 creator/launch-money）
  payments: ['payments'],
  // メール配信の画面（MailPage = 差出人・書く・送信の履歴）は mail だけ（2026-09-29 creator/mail-close）
  mail: ['mail']
};

/** ナビの項目（ルートの末尾 = 'leaderboards' 等）を出さないか */
export function isNavHidden(to: string): boolean {
  const features = NAV_FEATURES[to];
  return Boolean(features && features.length && features.every(isLaunchHidden));
}

/**
 * お金の画面（pages/money/MoneyShell.tsx の NAV と App.tsx の /money の子ルート）の区切り → その画面が見せる機能（2026-09-29 creator/launch-money）。
 * すべてが隠す機能なら、ナビの項目もルートも出さない（ProjectShell の NAV_FEATURES と同じ考え方）。
 *   earnings / sales / withdraw / get-paid / passkeys … 売上と出金（payouts）。passkeys は**出金の確認用**のパスキー
 *     （開発者のログインのパスキー = /account の方は別物で出したまま）
 *   three-d-secure … 出したまま（前払いの入金の 3DS。画面の中の「アプリ内の購入」の欄だけを payments で隠す = isPartHidden('payments.threeDSecure')）
 *   prepaid / usage … 前払い・利用料 = 初回の芯（ここに載せない = 常に出す）
 * 中身の文言は Money の持ち物なので触らない。ここは入口（ナビ・ルート）だけ
 */
const MONEY_NAV_FEATURES: Record<string, readonly string[]> = {
  earnings: ['payouts'],
  sales: ['payouts'],
  withdraw: ['payouts'],
  'get-paid': ['payouts'],
  passkeys: ['payouts']
};

/** お金の画面の区切り（URL の末尾 = 'earnings' 等）を出さないか */
export function isMoneyNavHidden(section: string): boolean {
  const features = MONEY_NAV_FEATURES[section];
  return Boolean(features && features.length && features.every(isLaunchHidden));
}

/** お金の画面を開いたときの最初の画面（/money）。売上が見せられるなら売上、隠れていれば前払い残高 */
export function moneyHomePath(): string {
  return isMoneyNavHidden('earnings') ? '/money/prepaid' : '/money/live/earnings';
}
